import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Menu, Flex, Dropdown, Button, Space } from "antd";
import {
  PlusOutlined,
  PlusSquareFilled,
  CodepenOutlined,
} from "@ant-design/icons";
import { SidebarThreadIcon, UnorderedListIcon } from "../../assets/icons";
import { useNavigate, useParams } from "react-router";
import ChannelListItem from "./ChannelListItem";
import DirectMessageListItem from "./DirectMessageListItem";
import CreateChannelModal from "./modals/CreateChannelModal";
import BrowseChannelModal from "./modals/BrowseChannelModel";
import DirectMessageModal from "./modals/DirectMessageModal";
import { handleApiError } from "../../utils/CommonFunctions";
import ApiService from "../../API/apiService";
import { API_ENDPOINTS } from "../../API/endpoints";
import FindChannelModal from "./modals/FindChannelModal";
import { useBridgeStore } from "../../store/apis/useBridgeStore";
import { Helmet } from "react-helmet";

const SidebarContainer = () => {
  const navigate = useNavigate();
  const { userId, name, channelId } = useParams();
  // const [openKeys, setOpenKeys] = useState(['sidebarMenuItem-1', 'sidebarMenuItem-2', 'sidebarMenuItem-3']);
  const [openKeys, setOpenKeys] = useState([
    "favorites",
    "channels",
    "direct-messages",
    "unread-messages",
  ]);

  const [UserData, setUserData] = useState(
    JSON.parse(localStorage.getItem("userData") || "{}")
  );

  const sidebarRefresh = useBridgeStore((state) => state.sidebarRefresh);
  const refreshType = useBridgeStore((state) => state.refreshType);
  // const [localdata, setlocaldata] = useState(JSON.parse(localStorage.getItem("userData")))
  const setJumpConfig = useBridgeStore((state) => state.setJumpConfig);
  const draftRefresh = useBridgeStore((state) => state.draftRefresh);
  let rout = UserData?.domain || "elsnertech";

  // States
  const [loading, setLoading] = useState(false);
  const [modals, setModals] = useState({
    isCreateChannelModalOpen: false,
    isDirectMessageSearchModalOpen: false,
    isBrowseChannelModalOpen: false,
    isFindChannelModalOpen: false,
  });
  const [channelList, setChannelList] = useState([]);
  const [chatList, setChatList] = useState([]);
  const [favUserData, setFavUserData] = useState([]);
  const [favChannelData, setFavChannelData] = useState([]);
  const [unreadThreadCount, setUnreadThreadCount] = useState(0);
  const [allNotificationCount, setallNotificationCount] = useState(0);
  const inputRef = useRef(null);
  const etItems = [];

  // Modal handlers
  const toggleModal = (modalName, value) => {
    setModals((prev) => ({
      ...prev,
      [modalName]: value !== undefined ? value : !prev[modalName],
    }));
  };

  // API calls
  const getUnreadThreadCountApi = useCallback(async () => {
    try {
      const response = await ApiService.get(API_ENDPOINTS.unreadThreadsCount);
      if (response?.data && response?.statusCode === 200) {
        setUnreadThreadCount(response.data.count);
      }
    } catch (error) {
      console.log("Error getting unread thread count:", error);
    }
  }, []);

  const getChannelListApi = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiService.get(API_ENDPOINTS.getChannel);
      if (response?.data && response?.statusCode === 200) {
        const newData = response.data;

        setChannelList(newData);

        const defaultItem = newData.find((item) => item.isDefault);
        if (defaultItem?._id) {
          localStorage.setItem("defaultChannelId", defaultItem._id);
        }
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getChatListApi = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiService.get(API_ENDPOINTS.getChatList, {
        userId: UserData?._id,
      });
      if (response?.data && response?.statusCode === 200) {
        setChatList(response.data.chatList || []);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, [UserData?._id]);

  const getFavListApi = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiService.get(API_ENDPOINTS.getFavoriteChatList, {
        userId: UserData?._id,
      });
      if (response?.data && response?.statusCode === 200) {
        setFavChannelData(response.data.favoriteChannels);
        setFavUserData(response.data.chatList);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, [UserData?._id]);

  // useEffect(()=>{
  //   getChannelListApi()
  //   getFavListApi()
  //   getChatListApi()
  // },[])
  useEffect(() => {
    getUnreadThreadCountApi();
  }, [getUnreadThreadCountApi]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (refreshType === "all") {
          await Promise.all([
            getChannelListApi(),
            getChatListApi(),
            getFavListApi(),
            getUnreadThreadCountApi(),
          ]);
        } else {
          switch (refreshType) {
            case "chat":
              await getChatListApi();
              break;
            case "channel":
              await getChannelListApi();
              break;
            case "favorite":
              await getFavListApi();
              break;
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sidebarRefresh, refreshType]);

  useEffect(() => {
    if (modals.isFindChannelModalOpen && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [modals.isFindChannelModalOpen]);

  // Event handlers
  const handleThreadClick = () => navigate(`/${rout}/unread/threads`);

  const onOpenChange = (keys) => {
    setOpenKeys(keys);
  };
  const handleDirectMessageClick = (item, isThreadUnSeen = 0) => {
    if (item?._id) {
      navigate(`/${rout}/message/${item._id}`, {
        state: {
          isThreadUnSeen: Boolean(isThreadUnSeen),
        },
      });
      setJumpConfig({});
      localStorage.setItem("fromPath", `/${rout}/message/${item._id}`);
    }
  };

  const handleChannelClick = (channel, isThreadUnSeen = 0) => {
    if (channel?._id) {
      navigate(`/${rout}/channels/${channel._id}`, {
        state: {
          isThreadUnSeen: Boolean(isThreadUnSeen),
        },
      });
      setJumpConfig({});
    }
  };

  const handleDraftClick = useCallback(() => {
    navigate(`/${rout}/draft`);
    setJumpConfig({});
  }, [navigate]);

  const [draftCount, setDraftCount] = useState(0);

  const getTotalValidDraftCount = () => {
    const count = Object.keys(localStorage)
      .filter((key) => key.startsWith("draft-"))
      .map((key) => {
        const value = localStorage.getItem(key);
        try {
          const parsed = JSON.parse(value);
          if (
            typeof parsed === "object" &&
            parsed !== null &&
            parsed.inputValue &&
            parsed.inputValue.trim() !== ""
          ) {
            return parsed;
          }
        } catch {
          // Ignore invalid JSON
        }
        return null;
      })
      .filter(Boolean).length;

    return count;
  };

  useEffect(() => {
    const count = getTotalValidDraftCount();
    setDraftCount(count);
  }, [draftRefresh]);

  // Menu items construction
  const buildSidebarMenuItems = () => {
    const hasFavorites = favUserData.length > 0 || favChannelData.length > 0;
    const selectedChannel = channelList.find(
      (channel) => channel._id === channelId
    );
    const selectedChat = chatList.find((chat) => chat._id === userId);

    // Build unread items
    const unreadItems = [];

    // Unread channels from channelList
    const unreadChannels = channelList.filter(
      (channel) => channel.unreadCount > 0
    );
    if (unreadChannels.length > 0) {
      unreadItems.push(
        ...unreadChannels.map((channel) => ({
          key: `unread-channel-${channel._id}`,
          label: (
            <ChannelListItem
              key={channel._id}
              channel={channel}
              getFavListApi={getFavListApi}
              getChannelListApi={getChannelListApi}
              isFavoriteList={false}
            />
          ),
          className: name === channel._id ? "ant_menu_item_selected" : "",
          icon: channel.isPrivate ? (
            <i className="fa-solid fa-lock" style={{ color: "#FFFFFFB8" }} />
          ) : (
            <i className="fa-solid fa-globe" />
          ),
          onClick: () => handleChannelClick(channel, unreadThreadCount),
        }))
      );
    }

    // Unread direct messages from chatList
    const unreadChats = chatList.filter((chat) => chat.unseenMessagesCount > 0);
    if (unreadChats.length > 0) {
      unreadItems.push(
        ...unreadChats.map((user, index) => ({
          key: `unread-dm-${user._id}`,
          label: (
            <DirectMessageListItem
              key={user._id}
              item={user}
              getFavListApi={getFavListApi}
              getChatListApi={getChatListApi}
              isFavoriteList={false}
            />
          ),
          className: userId === user._id ? "ant_menu_item_selected" : "",
          onClick: () => handleDirectMessageClick(user, unreadThreadCount),
        }))
      );
    }

    return [
      {
        key: "threads",
        label: (
          <Flex align="center" gap="10px" className="thread">
            <span className="Thread-icon">
              <SidebarThreadIcon />
            </span>
            <div className="msg-wrapper">
              <span className="name">Threads</span>
              {unreadThreadCount > 0 && (
                <span className="unread-msg-count">{unreadThreadCount}</span>
              )}
            </div>
          </Flex>
        ),
        onClick: handleThreadClick,
      },

      {
        key: "draft",
        label: (
          <Flex align="center" gap="10px" className="thread">
            <span className="Thread-icon">
              <CodepenOutlined />
            </span>
            <div className="msg-wrapper">
              <span className="name">Draft</span>
              {draftCount > 0 && (
                <span className="unread-msg-count">{draftCount}</span>
              )}
            </div>
          </Flex>
        ),
        onClick: handleDraftClick,
      },

      // Unread Messages section (conditional)
      {
        key: "unread-messages",
        label: (
          <Flex align="center" gap="small">
            <span className="icon-arrow">
              <i className="fa-solid fa-angle-right"></i>
            </span>
            <span>Unread Messages</span>
            {unreadItems.length > 0 && (
              <span className="unread-msg-count">{unreadItems.length}</span>
            )}
          </Flex>
        ),
        children: unreadItems,
      },

      // Favorites section (conditional)
      ...(hasFavorites
        ? [
            {
              key: "favorites",
              label: (
                <Flex align="center" gap="small">
                  <span className="icon-arrow">
                    <i className="fa-solid fa-angle-right"></i>
                  </span>
                  <span>Favorites</span>
                </Flex>
              ),
              children: [
                ...favUserData.map((item, index) => ({
                  key: `sidebarMenuItem-${index}`,
                  label: (
                    <DirectMessageListItem
                      key={item._id}
                      item={{ ...item, isFavorite: true }}
                      getFavListApi={getFavListApi}
                      getChatListApi={getChatListApi}
                      isFavoriteList={true}
                    />
                  ),
                  className: name === item._id ? "ant_menu_item_selected" : "",
                  onClick: () => handleDirectMessageClick(item),
                })),
                ...(favChannelData || []).map((channel, index) => ({
                  key: `channel-${index}`,
                  label: (
                    <ChannelListItem
                      key={channel._id}
                      channel={{
                        ...channel,
                        isFavorite: true,
                        unreadCount:
                          channel.unseenMessagesCount || channel.unreadCount,
                      }}
                      getFavListApi={getFavListApi}
                      getChannelListApi={getChannelListApi}
                      isFavoriteList={true}
                    />
                  ),
                  className:
                    name === channel._id ? "ant_menu_item_selected" : "",
                  icon: channel.isPrivate ? (
                    <i
                      className="fa-solid fa-lock"
                      style={{ color: "#FFFFFFB8" }}
                    />
                  ) : (
                    <i className="fa-solid fa-globe" />
                  ),
                  onClick: () => handleChannelClick(channel),
                })),
              ],
            },
          ]
        : []),

      // Channels section
      {
        key: "channels",
        label: (
          <Flex align="center" gap="small">
            <span className="icon-arrow">
              <i className="fa-solid fa-angle-right"></i>
            </span>
            <span>Channels</span>
          </Flex>
        ),
        children: [
          // Only show channels with no unread messages
          ...(channelList || [])
            .filter((channel) => channel.unreadCount === 0)
            .map((channel) => ({
              key: `channel-${channel._id}`,
              label: (
                <ChannelListItem
                  key={channel._id}
                  channel={channel}
                  getFavListApi={getFavListApi}
                  getChannelListApi={getChannelListApi}
                  isFavoriteList={false}
                />
              ),
              className: name === channel._id ? "ant_menu_item_selected" : "",
              icon: channel.isPrivate ? (
                <i
                  className="fa-solid fa-lock"
                  style={{ color: "#FFFFFFB8" }}
                />
              ) : (
                <i className="fa-solid fa-globe" />
              ),
              onClick: () => handleChannelClick(channel),
            })),
          {
            key: "add-channel",
            label: (
              <Flex justify="space-between" align="center" gap="middle">
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "create-channel",
                        label: "Create new channel",
                        icon: (
                          <PlusOutlined
                            style={{ fontSize: "14px", color: "#A1A1A1" }}
                          />
                        ),
                        onClick: () =>
                          toggleModal("isCreateChannelModalOpen", true),
                      },
                      {
                        key: "browse-channels",
                        label: "Browse channels",
                        icon: <UnorderedListIcon />,
                        onClick: () =>
                          toggleModal("isBrowseChannelModalOpen", true),
                      },
                    ],
                  }}
                  className="ec-dropdown sidebar-channel-dropdown"
                  dropdownRender={(menu) => (
                    <div className="ec-dropdown__menu sidebar-channel-dropdown__menu">
                      {menu}
                    </div>
                  )}
                  trigger={["click"]}
                >
                  <span>
                    <PlusSquareFilled /> Add Channel
                  </span>
                </Dropdown>
              </Flex>
            ),
            className: "add-channel-btn",
          },
        ],
      },

      ...(selectedChannel && !openKeys.includes("channels")
        ? [
            {
              key: `selected-channel-${selectedChannel._id}`,
              label: (
                <ChannelListItem
                  key={selectedChannel._id}
                  channel={selectedChannel}
                  getFavListApi={getFavListApi}
                  getChannelListApi={getChannelListApi}
                  hideMoreOptions={true}
                />
              ),
              className: "ant_menu_item_selected static-item-selected",
              icon: selectedChannel.isPrivate ? (
                <i
                  className="fa-solid fa-lock"
                  style={{ color: "#FFFFFFB8" }}
                />
              ) : (
                <i className="fa-solid fa-globe" />
              ),
              onClick: () => handleChannelClick(selectedChannel),
            },
          ]
        : []),

      // Direct Messages section
      {
        key: "direct-messages",
        label: (
          <Flex justify="space-between" align="center" gap="small">
            <Flex
              align="center"
              gap="small"
              onClick={(e) => {
                e.stopPropagation();
                if (!openKeys.includes("direct-messages")) {
                  onOpenChange([...openKeys, "direct-messages"]);
                } else {
                  onOpenChange(
                    openKeys.filter((key) => key !== "direct-messages")
                  );
                }
              }}
              style={{ cursor: "pointer", flex: 1 }}
            >
              <span className="icon-arrow">
                <i className="fa-solid fa-angle-right"></i>
              </span>
              <span>Direct Messages</span>
            </Flex>
            <Button
              type="text"
              className="add-direct-msg-btn"
              onClick={(e) => {
                e.stopPropagation();
                toggleModal("isDirectMessageSearchModalOpen", true);
              }}
            >
              <PlusOutlined />
            </Button>
          </Flex>
        ),
        children: [
          // Only show chats with no unseen messages
          ...(chatList || [])
            .filter((user) => user.unseenMessagesCount === 0)
            .map((user, index) => ({
              key: `direct-message-${index}`,
              label: (
                <DirectMessageListItem
                  key={user._id}
                  item={user}
                  getFavListApi={getFavListApi}
                  getChatListApi={getChatListApi}
                  isFavoriteList={false}
                />
              ),
              className: userId === user._id ? "ant_menu_item_selected" : "",
              onClick: () => handleDirectMessageClick(user),
            })),
        ],
      },

      ...(selectedChat && !openKeys.includes("direct-messages")
        ? [
            {
              key: `selected-chat-${selectedChat._id}`,
              label: (
                <DirectMessageListItem
                  key={selectedChat._id}
                  item={selectedChat}
                  getFavListApi={getFavListApi}
                  getChatListApi={getChatListApi}
                  hideMoreOptions={true}
                />
              ),
              className: "ant_menu_item_selected static-item-selected",
              onClick: () => handleDirectMessageClick(selectedChat),
            },
          ]
        : []),
    ].filter(Boolean);
  };

  const sidebarMenuItems = buildSidebarMenuItems();
  const totalCount = useMemo(() => {
    const sumUnread = (users, property) =>
      users.reduce((total, user) => total + (user[property] || 0), 0);
    return (
      sumUnread(channelList, "unreadCount") +
      sumUnread(chatList, "unseenMessagesCount") +
      sumUnread(favUserData, "unseenMessagesCount") +
      sumUnread(favChannelData, "unseenMessagesCount")
    );
  }, [channelList, chatList, favUserData, favChannelData]);
  useEffect(() => {
    setallNotificationCount(totalCount);
  }, [totalCount]);

  useEffect(() => {
    document.title =
      allNotificationCount > 0
        ? `(${allNotificationCount})* New Messages`
        : `Welcome to ${UserData?.companyName} e-Connect Portal`;

    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) {
      favicon.href =
        allNotificationCount > 0 ? "/faviconN.png" : "/favicon-Defualt.png";
    } else {
      const link = document.createElement("link");
      link.rel = "icon";
      link.href =
        allNotificationCount > 0 ? "/faviconN.png" : "/favicon-Defualt.png";
      document.head.appendChild(link);
    }

    const observer = new MutationObserver(() => {
      if (favicon)
        favicon.href =
          allNotificationCount > 0 ? "/faviconN.png" : "/favicon-Defualt.png";
    });

    observer.observe(document.head, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [allNotificationCount]);

  // ✅ Update PWA Notification Badge
  useEffect(() => {
    if ("setAppBadge" in navigator && "clearAppBadge" in navigator) {
      if (allNotificationCount > 0) {
        navigator
          .setAppBadge(allNotificationCount)
          .catch((err) => console.error("Badge API Error:", err));
      } else {
        navigator
          .clearAppBadge()
          .catch((err) => console.error("Badge API Error:", err));
      }
    }
  }, [allNotificationCount]);
  return (
    <>
      <Helmet>
        <title>
          {allNotificationCount > 0
            ? `(${allNotificationCount})* New Messages`
            : `Welcome to ${UserData?.companyName} e-Connect Portal`}
        </title>
      </Helmet>
      <Flex
        justify="space-between"
        align="center"
        gap="small"
        className="sidebar-header"
      >
        <Dropdown
          menu={{ items: etItems }}
          className="ec-dropdown et-dropdown"
          placement="top"
        >
          <Space style={{ cursor: "default" }}>
            {UserData && UserData?.companyName
              ? UserData?.companyName
              : "Elsner technologies"}
          </Space>
        </Dropdown>
      </Flex>

      <Flex align="center" gap="small" className="sidebar-filters-wrapper">
        <Button
          className="search-channel-btn"
          onClick={() => toggleModal("isFindChannelModalOpen", true)}
        >
          <i className="fa-solid fa-magnifying-glass search-icon"></i>
          <span>Start Chat</span>
        </Button>
      </Flex>

      {/* Modals */}
      {modals.isFindChannelModalOpen && <FindChannelModal
        open={modals.isFindChannelModalOpen}
        onCancel={() => toggleModal("isFindChannelModalOpen", false)}
        channelList={channelList}
        chatList={chatList}
      />}

      <div className="sidebar-categories">
        <div className="scroll-view">
          <Menu
            onOpenChange={onOpenChange}
            className="sidebar-menu"
            mode="inline"
            openKeys={openKeys}
            items={sidebarMenuItems}
            style={{ marginTop: "0px" }}
          />
        </div>

        {modals.isCreateChannelModalOpen && <CreateChannelModal
          isOpen={modals.isCreateChannelModalOpen}
          onClose={() => toggleModal("isCreateChannelModalOpen", false)}
          getChannelListApi={getChannelListApi}
        />}

        {modals.isBrowseChannelModalOpen && <BrowseChannelModal
          open={modals.isBrowseChannelModalOpen}
          onCancel={() => toggleModal("isBrowseChannelModalOpen", false)}
          getChannelListApi={getChannelListApi}
        />}
        {modals.isDirectMessageSearchModalOpen && <DirectMessageModal
          open={modals.isDirectMessageSearchModalOpen}
          onCancel={() => toggleModal("isDirectMessageSearchModalOpen", false)}
          getChatListApi={getChatListApi}
          getFavListApi={getFavListApi}
        />}

        {/* <DirectMessageModal
          open={modals.isDirectMessageSearchModalOpen}
          onCancel={() => toggleModal('isDirectMessageSearchModalOpen', false)}
          getChatListApi={getChatListApi}
        /> */}
      </div>
    </>
  );
};

export default SidebarContainer;
